import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { issuesService } from '@/services/api/issues.service';
import { SUCCESS } from '@/constants';
import type { IssueListQuery, RaiseIssueRequest, UpdateIssueRequest } from '@/types';
import { queryKeys } from './keys';

export function useIssues(query: IssueListQuery) {
  return useQuery({
    queryKey: queryKeys.issues.list(query),
    queryFn: ({ signal }) => issuesService.list(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useRaiseIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RaiseIssueRequest) => issuesService.raise(body),
    onSuccess: async () => {
      toast.success('Issue raised.');
      /*
       * Invalidates the whole issues key rather than one filtered list. The new
       * issue's severity and status decide which of the open filters it belongs
       * to, and working that out here would duplicate the server's ordering.
       */
      await queryClient.invalidateQueries({ queryKey: queryKeys.issues.all });
      // An open issue is one of the dashboard's counters.
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useUpdateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, body }: { issueId: string; body: UpdateIssueRequest }) =>
      issuesService.update(issueId, body),
    onSuccess: async () => {
      toast.success(SUCCESS.saved);
      await queryClient.invalidateQueries({ queryKey: queryKeys.issues.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}
